import { ColDef, CellMouseOverEvent, GridApi, GridOptions, ICellRendererParams } from '@ag-grid-community/core';
import { Connect } from '@genesislcap/foundation-comms';
import { attr, customElement, GenesisElement, observable } from '@genesislcap/web-core';
import { Modal } from '@genesislcap/foundation-ui';
import { MaturityAmountEntry } from '../../cell-renderers/maturity-amount-entry';
import { MaturityOrder, OrderStatusEnum, successOrderStatuses, QuantityCellValidationState } from '../deal-series.types';
import {
  allowOnlyWholePositiveNumbers,
  conditionsTooltipFormatter,
  formatOrderQuantityForCell,
  getOrderAmountCap,
  getOrderPatchAfterAllClear,
  getOrderStatusAfterSubmittedTrackConditionsEdit,
  getOrderStatusAfterSubmittedTrackManualEdit,
  hasSubmittedAmountOnRecord,
  hasZeroCouponPrincipalAmount,
  isFullParOrderAmountApplied,
  isZeroCouponMaturity,
  normalizeNumericValueForDisplay,
  normalizeNumericValueForStorage,
  resolveManualOrderQuantityEdit,
  mapMaturityOrderForDeletion,
  mapMaturityOrderForSubmission,
  shouldHighlightPrincipalAmountOnHover,
} from '../deal-series.util';
import {
  areAllSplitChildrenAtFullCap,
  childBondOrderToSplitRow,
  buildInitialSplitRowsForOpen,
  buildCollapsedLoneOrderFromSplitChild,
  createNewSplitRowOrder,
  getSplitOrderSaveState,
  shouldShowRemoveSplit,
  fetchBondOrderChildren,
  formatSplitOrderAmountTotal,
  getSplitConditionsRollupOrder,
  getSplitParentQuantityTotal,
  isSplitMaturityGridOrder,
  isSplitRowBlank,
  isSplitRowEditable,
  isSplitRowRemovable,
  hasInTransitSplitRows,
  mergeInTransitSplitRowsFromFetchedChildren,
  applyFetchedChildrenToSplitRows,
  persistedSplitRowDiffersFromAcceptedBaseline,
  restoreSplitRowToAcceptedBaseline,
  resolveSplitRowQuantity,
  shouldShowSplitRowOrderStatus,
  splitRowHasEditsFromAcceptedBaseline,
} from '../split-order.util';
import { getFixedWidthConfig } from '../../deals.util';
import { notifyApiError } from '../../../../utils/utils';
import { splitOrderModalStyles as styles } from './split-order-modal.styles';
import { splitOrderModalTemplate as template } from './split-order-modal.template';
import { SplitOrderRow } from './split-order-modal.types';
import {
  SplitOrderAmountCellRenderer,
  SplitOrderConditionsCellRenderer,
  SplitOrderRowActionsCellRenderer,
} from './split-order-cell-renderers';
import { DealConditionsForm } from '../deal-conditions-form/deal-conditions-form';
import {
  buildSplitOrderReadOnlyColumnDefs,
  getSplitOrderAddSplitOffset,
  getSplitOrderGridMinWidth,
} from './split-order-modal.col-defs';
import {
  conditionsSummaryColDef,
  dealSeriesBorder,
  lightGreyCell,
  orderEntryCellStyle,
  statusColDef,
} from '../deal-series.col-defs';
import { getStatusIconConfig, getQuantityCellValidationState } from '../../deal-detail/deal-reconfirm.utils';
import { isRepriceWindow } from '../deal-status';
import {
  SPLIT_ORDER_COL_WIDTHS,
  SPLIT_ORDER_MODAL_HORIZONTAL_PADDING,
  SPLIT_ORDER_MODAL_MAX_HEIGHT,
} from './split-order-modal.styles';
import { TaxStatusEnum } from '../../deals.types';
import {
  computeSinkingFundPanelLayout,
  isTermBondMaturity,
} from '../sinking-fund-schedule.util';
import {
  getQuantityColumnValidationColDef,
  getSplitRowValidationOrder,
} from '../deal-series.quantity-validation';

const GRID_ROW_HEIGHT = 40;
const GRID_HEADER_HEIGHT = 40;
const GRID_SCROLLBAR_BUFFER_PX = 17;
/** Modal chrome outside grid: title, footer, add row, gaps. */
const SPLIT_ORDER_MODAL_NON_GRID_PX = 158;
const STATUS_POLL_INTERVAL_MS = 5_000;

const getSplitRowStatusIconConfig = (
  params: ICellRendererParams<SplitOrderRow>,
  dealStatus: string | undefined,
  sourceOrder: MaturityOrder,
  splitRows: SplitOrderRow[],
) => {
  if (!shouldShowSplitRowOrderStatus(params.data)) {
    return null;
  }
  const order =
    params.data?.rowType === 'source'
      ? getSplitConditionsRollupOrder(sourceOrder, splitRows)
      : params.data?.order;
  if (!order) {
    return null;
  }
  return getStatusIconConfig({ data: order } as ICellRendererParams<MaturityOrder>, dealStatus);
};

const getSplitRowStatusTooltipOrder = (
  row: SplitOrderRow | undefined,
  sourceOrder: MaturityOrder,
  splitRows: SplitOrderRow[],
): MaturityOrder | null => {
  if (!row || !shouldShowSplitRowOrderStatus(row)) {
    return null;
  }
  if (row.rowType === 'source') {
    return getSplitConditionsRollupOrder(sourceOrder, splitRows);
  }
  return row.order ?? null;
};

@customElement({
  name: 'split-order-modal',
  template,
  styles,
})
export class SplitOrderModal extends GenesisElement {
  @Connect connect: Connect;

  modal: Modal;

  confirmRemoveSplitModal: Modal;

  gridContainer: HTMLElement;

  conditionsPopup: HTMLElement;

  conditionsForm: DealConditionsForm;

  private gridApi: GridApi;

  private nextSplitRowId = 0;

  @observable gridOptions: GridOptions;

  @observable modalOpen = false;

  @observable loading = false;

  @observable splitRows: SplitOrderRow[] = [];

  @observable conditionsActive = false;

  @observable orderEditConditions: MaturityOrder;

  private pendingSplitAmounts = new Map<string, string>();

  /** Bumped when split amounts change so Save Split eligibility re-evaluates. */
  @observable private splitSaveEvaluationToken = 0;

  get isConditionsOrderAmountLocked(): boolean {
    return !!this.orderEditConditions && isFullParOrderAmountApplied(this.orderEditConditions);
  }

  baselineChildIds: Set<string> = new Set();

  baselineChildren: MaturityOrder[] = [];

  private conditionsEditRowId: string;

  @attr({ attribute: 'series-code' }) seriesCode: string;

  @attr({ attribute: 'series-size' }) seriesSize: string;

  @attr({ attribute: 'tax-status' }) taxStatus: string;

  @attr({ attribute: 'security-type' }) securityType: string;

  @attr({ attribute: 'deal-status' }) dealStatus: string;

  dealStatusChanged(): void {
    if (!this.gridApi) return;
    this.gridApi.refreshCells({ columns: ['splitAmount'], force: true });
  }

  @attr({ attribute: 'deal-has-eligible-orders-to-reset', mode: 'boolean' })
  dealHasEligibleOrdersToReset: boolean;

  taxStatusChanged(): void {
    if (this.modalOpen) {
      this.setGridOptions();
    }
  }

  @observable addSplitOffset = 0;

  @observable flexMaturityOptions: MaturityOrder[] = [];

  @observable sinkingFundModalVisible = false;

  @observable sinkingFundScheduleJson: string | null = null;

  @observable sinkingFundPanelStyle = '';

  @observable sinkingFundPanelHovered = false;

  private sinkingFundHideTimeout: number | null = null;

  @observable principalAmountModalVisible = false;

  @observable principalAmountValue: string | null = null;

  @observable principalAmountHighlighted = false;

  @observable principalAmountAnchorTop = 0;

  @observable principalAmountAnchorLeft = 0;

  private sourceOrder: MaturityOrder;

  private handleDocumentClickBound: ((e: Event) => void) | null = null;

  private statusPollInterval: ReturnType<typeof setInterval> | null = null;

  private statusRefreshInFlight = false;

  connectedCallback(): void {
    super.connectedCallback();
    this.removeAttribute('open');
    this.handleDocumentClickBound = this.handleDocumentClick.bind(this);
    document.addEventListener('click', this.handleDocumentClickBound, true);
  }

  disconnectedCallback(): void {
    this.stopStatusPolling();
    document.removeEventListener('click', this.handleDocumentClickBound, true);
    super.disconnectedCallback();
  }

  async open(sourceOrder: MaturityOrder): Promise<void> {
    this.sourceOrder = sourceOrder;
    this.nextSplitRowId = 0;
    this.baselineChildIds = new Set();
    this.baselineChildren = [];
    this.conditionsActive = false;
    this.orderEditConditions = null;
    this.setGridOptions();
    if (!this.modal?.show) {
      console.error('split-order-modal: rapid-modal ref not available');
      return;
    }
    this.modalOpen = true;
    this.loading = true;
    this.splitRows = [];
    this.setAttribute('open', '');
    requestAnimationFrame(() => this.modal?.show());
    await this.preloadSplitRows(sourceOrder);
    this.loading = false;
    this.updateGridData();
    this.syncInTransitPolling();
    this.notifySplitSaveEvaluationChanged();
  }

  private async preloadSplitRows(sourceOrder: MaturityOrder): Promise<void> {
    const shouldLoadChildren =
      isSplitMaturityGridOrder(sourceOrder) || !!String(sourceOrder.ORDER_ID ?? '').trim();
    const children = shouldLoadChildren
      ? await fetchBondOrderChildren(this.connect, sourceOrder.MATURITY_ID)
      : [];

    if (children.length > 0) {
      this.baselineChildren = children;
      this.baselineChildIds = new Set(children.map((c) => c.ORDER_ID).filter(Boolean));
      this.splitRows = [
        { rowId: 'source', rowType: 'source', splitAmount: '', order: { ...sourceOrder } },
        ...children.map((child) =>
          childBondOrderToSplitRow(sourceOrder, child, `split-${++this.nextSplitRowId}`),
        ),
      ];
      return;
    }

    if (isSplitMaturityGridOrder(sourceOrder)) {
      // Parent grid QUANTITY is the rollup across split lines — never treat it as one split row.
      this.baselineChildren = [];
      this.baselineChildIds = new Set();
      this.splitRows = [{ rowId: 'source', rowType: 'source', splitAmount: '', order: { ...sourceOrder } }];
      return;
    }

    const nextSplitRowId = { value: this.nextSplitRowId };
    this.splitRows = buildInitialSplitRowsForOpen(sourceOrder, nextSplitRowId);
    this.nextSplitRowId = nextSplitRowId.value;
  }

  async refreshFromReqRep(
    sourceOrder?: MaturityOrder,
    options?: { syncPersistedFromServer?: boolean; syncPersistedOrderIds?: Iterable<string> },
  ): Promise<void> {
    if (sourceOrder) {
      this.sourceOrder = sourceOrder;
    }
    if (!this.sourceOrder) return;
    const children = await fetchBondOrderChildren(this.connect, this.sourceOrder.MATURITY_ID);
    const nextSplitRowId = { value: this.nextSplitRowId };
    this.splitRows = applyFetchedChildrenToSplitRows(
      this.sourceOrder,
      this.splitRows,
      children,
      {
        nextSplitRowId,
        syncPersistedFromServer: options?.syncPersistedFromServer,
        syncPersistedOrderIds: options?.syncPersistedOrderIds,
      },
    );
    this.nextSplitRowId = nextSplitRowId.value;
    if (options?.syncPersistedOrderIds) {
      const syncOrderIds = new Set(
        [...options.syncPersistedOrderIds]
          .map((orderId) => String(orderId ?? '').trim())
          .filter(Boolean),
      );
      const childrenByOrderId = new Map(
        this.baselineChildren
          .filter((child) => String(child.ORDER_ID ?? '').trim() !== '')
          .map((child) => [String(child.ORDER_ID), child]),
      );
      for (const child of children) {
        const orderId = String(child.ORDER_ID ?? '').trim();
        if (orderId && syncOrderIds.has(orderId)) {
          childrenByOrderId.set(orderId, child);
        }
      }
      this.baselineChildren = Array.from(childrenByOrderId.values()).sort(
        (left, right) => (left.LINE_NO ?? 0) - (right.LINE_NO ?? 0),
      );
    } else {
      this.baselineChildren = children;
    }
    this.baselineChildIds = new Set(children.map((child) => child.ORDER_ID).filter(Boolean));
    this.updateGridData();
    this.refreshOrderStatusCells();
    this.refreshSplitAmountValidation();
    this.syncInTransitPolling();
    this.notifySplitSaveEvaluationChanged();
  }

  /** One-shot sync after submit when local rows may still show DRAFT but server is in transit. */
  async kickInTransitStatusSync(): Promise<void> {
    await this.pollOrderStatusFromServer(true);
  }

  private syncInTransitPolling(): void {
    if (!this.modalOpen) {
      this.stopStatusPolling();
      return;
    }
    if (hasInTransitSplitRows(this.splitRows)) {
      if (this.statusPollInterval == null) {
        this.statusPollInterval = setInterval(() => {
          void this.pollOrderStatusFromServer();
        }, STATUS_POLL_INTERVAL_MS);
      }
    } else {
      this.stopStatusPolling();
    }
  }

  private stopStatusPolling(): void {
    if (this.statusPollInterval != null) {
      clearInterval(this.statusPollInterval);
      this.statusPollInterval = null;
    }
  }

  private shouldSkipStatusPoll(): boolean {
    if (!this.modalOpen || this.loading || !this.sourceOrder) {
      return true;
    }
    if (this.conditionsActive) {
      return true;
    }
    const activeElement = document.activeElement;
    if (
      activeElement instanceof HTMLInputElement &&
      this.gridContainer?.contains(activeElement) &&
      activeElement.closest('maturity-amount-entry')
    ) {
      return true;
    }
    return false;
  }

  private async pollOrderStatusFromServer(forceFetch = false): Promise<void> {
    if (this.shouldSkipStatusPoll() || this.statusRefreshInFlight) {
      return;
    }
    if (!forceFetch && !hasInTransitSplitRows(this.splitRows)) {
      this.syncInTransitPolling();
      return;
    }
    this.statusRefreshInFlight = true;
    try {
      const children = await fetchBondOrderChildren(this.connect, this.sourceOrder.MATURITY_ID);
      if (children.length === 0) {
        this.syncInTransitPolling();
        return;
      }

      const { rows, changed, mergedOrderIds } = mergeInTransitSplitRowsFromFetchedChildren(
        this.sourceOrder,
        this.splitRows,
        children,
      );
      if (changed) {
        if (mergedOrderIds.length > 0) {
          const mergedIdSet = new Set(mergedOrderIds);
          const byOrderId = new Map(
            this.baselineChildren
              .filter((child) => String(child.ORDER_ID ?? '').trim() !== '')
              .map((child) => [String(child.ORDER_ID), child]),
          );
          for (const child of children) {
            const orderId = String(child.ORDER_ID ?? '').trim();
            if (orderId && mergedIdSet.has(orderId)) {
              byOrderId.set(orderId, child);
            }
          }
          this.baselineChildren = Array.from(byOrderId.values()).sort(
            (left, right) => (left.LINE_NO ?? 0) - (right.LINE_NO ?? 0),
          );
        }
        this.baselineChildIds = new Set(children.map((child) => child.ORDER_ID).filter(Boolean));
        this.splitRows = rows;
        if (this.conditionsEditRowId) {
          this.syncConditionsEditorFromRow(this.conditionsEditRowId);
        }
        this.updateGridData();
        this.refreshOrderStatusCells();
        for (const orderId of mergedOrderIds) {
          const row = this.splitRows.find(
            (splitRow) => String(splitRow.order.ORDER_ID ?? '') === orderId,
          );
          if (row) {
            this.refreshSplitAmountValidation(row.rowId);
          }
        }
      }
    } finally {
      this.statusRefreshInFlight = false;
      this.syncInTransitPolling();
    }
  }

  handleGridReady(e: CustomEvent): void {
    this.gridApi = e.detail.event.api;
    this.updateGridData();
    this.updateGridLayout();
  }

  addSplitRow(): void {
    if (!this.sourceOrder) return;
    const rowId = `split-${++this.nextSplitRowId}`;
    this.splitRows = [
      ...this.splitRows,
      {
        rowId,
        rowType: 'split',
        splitAmount: '',
        order: createNewSplitRowOrder(this.sourceOrder),
      },
    ];
    this.updateGridData(rowId);
    this.notifySplitSaveEvaluationChanged();
  }

  removeSplitRow(rowId: string): void {
    const row = this.splitRows.find((r) => r.rowId === rowId);
    if (!row || !isSplitRowRemovable(row)) return;
    if (this.conditionsEditRowId === rowId) {
      this.closeConditionsPopup();
    }
    this.splitRows = this.splitRows.filter((r) => r.rowId !== rowId);
    this.updateGridData();
    this.refreshOrderStatusCells();
    this.notifySplitSaveEvaluationChanged();
  }

  private resolveNextSplitRowOrderStatus(
    row: SplitOrderRow,
    quantity: string,
    isZeroCancelIntent: boolean,
  ): OrderStatusEnum | undefined {
    const statusReference = row.acceptedOrderBaseline ?? row.order;
    const hasPersistedId = String(row.order.ORDER_ID ?? '').trim() !== '';
    if (hasPersistedId || row.acceptedOrderBaseline) {
      return getOrderStatusAfterSubmittedTrackManualEdit(
        {
          ...row.order,
          ORDER_STATUS: statusReference.ORDER_STATUS,
          SUBMITTED_QUANTITY: statusReference.SUBMITTED_QUANTITY,
        },
        quantity,
      );
    }
    if (isZeroCancelIntent) {
      return OrderStatusEnum.SUBMITTED_AND_DRAFT;
    }
    return row.order.ORDER_STATUS;
  }

  private applySplitAmount(rowId: string, value: string): void {
    this.pendingSplitAmounts.delete(rowId);
    const targetRow = this.splitRows.find((r) => r.rowId === rowId);
    if (!targetRow || !isSplitRowEditable(targetRow)) return;
    this.splitRows = this.splitRows.map((r) => {
      if (r.rowId !== rowId) return r;
      const { quantity, isZeroCancelIntent, shouldIgnore } = resolveManualOrderQuantityEdit(
        r.order,
        value,
      );
      if (shouldIgnore) return r;
      const orderWithQuantity: MaturityOrder = {
        ...r.order,
        QUANTITY: quantity,
        IS_ZERO_CANCEL_INTENT: isZeroCancelIntent,
      };
      const splitAmount = formatOrderQuantityForCell(orderWithQuantity);
      const orderForCapCheck = { ...r.order, QUANTITY: splitAmount };
      const atCap = isFullParOrderAmountApplied(orderForCapCheck);
      const nextOrderStatus = this.resolveNextSplitRowOrderStatus(r, quantity, isZeroCancelIntent);
      return {
        ...r,
        splitAmount,
        order: {
          ...r.order,
          QUANTITY: quantity,
          IS_ZERO_CANCEL_INTENT: isZeroCancelIntent,
          IS_AUTO_RESET_ORDER_AMOUNT: atCap ? !!r.order.IS_AUTO_RESET_ORDER_AMOUNT : false,
          ORDER_STATUS: nextOrderStatus,
        },
      };
    });
    this.applySplitRowGridRefresh(rowId);
  }

  private applySplitRowGridRefresh(rowId: string): void {
    this.syncConditionsEditorFromRow(rowId);
    const rowNode = this.gridApi?.getRowNode(rowId);
    const row = this.splitRows.find((splitRow) => splitRow.rowId === rowId);
    if (rowNode && row) {
      rowNode.setData({ ...row });
    }
    this.refreshSplitAmountValidation(rowId);
    this.refreshOrderStatusCells();
    this.refreshRowActionCells();
    this.notifySplitSaveEvaluationChanged();
  }

  private applySplitRowState(updatedRow: SplitOrderRow): void {
    this.pendingSplitAmounts.delete(updatedRow.rowId);
    this.splitRows = this.splitRows.map((row) =>
      row.rowId === updatedRow.rowId ? updatedRow : row,
    );
    this.applySplitRowGridRefresh(updatedRow.rowId);
  }

  private applyAllSplitAmount(rowId: string, isAll: boolean): void {
    const targetRow = this.splitRows.find((r) => r.rowId === rowId);
    if (!targetRow || !isSplitRowEditable(targetRow)) return;
    const cap = getOrderAmountCap(this.sourceOrder);
    this.splitRows = this.splitRows.map((row) => {
      if (row.rowId !== rowId) return row;
      if (isAll) {
        const splitAmount = normalizeNumericValueForDisplay(cap, row.order);
        const quantity = normalizeNumericValueForStorage(cap, row.order) ?? '';
        return {
          ...row,
          splitAmount,
          order: {
            ...row.order,
            QUANTITY: quantity,
            IS_AUTO_RESET_ORDER_AMOUNT: true,
            IS_ZERO_CANCEL_INTENT: false,
            ORDER_STATUS: getOrderStatusAfterSubmittedTrackManualEdit(row.order, quantity),
          },
        };
      }
      const clearedPatch = getOrderPatchAfterAllClear(row.order);
      return {
        ...row,
        splitAmount: '',
        order: {
          ...row.order,
          ...clearedPatch,
        },
      };
    });
    this.syncConditionsEditorFromRow(rowId);
    const row = this.splitRows.find((splitRow) => splitRow.rowId === rowId);
    const rowNode = this.gridApi?.getRowNode(rowId);
    if (rowNode && row) {
      rowNode.setData({ ...row });
    }
    const sourceRow = this.splitRows.find((splitRow) => splitRow.rowType === 'source');
    const pinnedTopRow = this.gridApi?.getPinnedTopRow(0);
    if (pinnedTopRow && sourceRow) {
      pinnedTopRow.setData({ ...sourceRow });
    }
    this.refreshSplitAmountValidation(rowId);
    this.refreshRowActionCells();
    this.refreshOrderStatusCells();
    this.notifySplitSaveEvaluationChanged();
  }

  private syncConditionsEditorFromRow(rowId: string): void {
    if (this.conditionsEditRowId !== rowId) return;
    const row = this.splitRows.find((splitRow) => splitRow.rowId === rowId);
    if (!row) return;

    const quantity = resolveSplitRowQuantity(row);
    const nextOrder: MaturityOrder = { ...row.order, QUANTITY: quantity };
    this.orderEditConditions = nextOrder;
    this.conditionsForm?.reflectExternalQuantity(nextOrder);
  }

  private getSplitRowQuantityValidationState(
    rowId: string,
    amount: string,
  ): QuantityCellValidationState {
    const row = this.splitRows.find((splitRow) => splitRow.rowId === rowId);
    const validationOrder = getSplitRowValidationOrder(
      row ? { ...row, splitAmount: amount } : undefined,
    );
    return validationOrder
      ? getQuantityCellValidationState(validationOrder, this.dealStatus)
      : { hasError: false, hasWarning: false, tooltip: null };
  }

  private refreshSplitAmountValidation(rowId?: string): void {
    if (!this.gridApi) return;
    const rowNodes = rowId
      ? [this.gridApi.getRowNode(rowId)].filter(Boolean)
      : undefined;
    this.gridApi.refreshCells({
      rowNodes,
      columns: ['splitAmount'],
      force: true,
    });
    const pinnedTopRow = this.gridApi.getPinnedTopRow(0);
    if (pinnedTopRow) {
      this.gridApi.refreshCells({
        rowNodes: [pinnedTopRow],
        columns: ['splitAmount'],
        force: true,
      });
    }
  }

  commitSplitAmount(rowId: string, value: string): void {
    this.applySplitAmount(rowId, allowOnlyWholePositiveNumbers(value));
  }

  async resetSplitAmount(rowId: string): Promise<void> {
    let row = this.splitRows.find((r) => r.rowId === rowId);
    if (!row) return;

    if (row.acceptedOrderBaseline && splitRowHasEditsFromAcceptedBaseline(row)) {
      const restored = restoreSplitRowToAcceptedBaseline(row);
      if (restored) {
        this.applySplitRowState(restored);
        if (!restored.order.ORDER_ID) {
          return;
        }
        const serverChild = this.baselineChildren.find(
          (child) => String(child.ORDER_ID ?? '') === String(restored.order.ORDER_ID ?? ''),
        );
        const needsServerReset =
          serverChild != null &&
          persistedSplitRowDiffersFromAcceptedBaseline(
            this.sourceOrder,
            serverChild,
            restored.acceptedOrderBaseline,
          );
        if (!needsServerReset) {
          return;
        }
        row = restored;
      }
    } else if (!row.order.ORDER_ID) {
      const restored = restoreSplitRowToAcceptedBaseline(row);
      if (restored) {
        this.applySplitRowState(restored);
        return;
      }
      this.commitSplitAmount(rowId, '');
      return;
    }

    if (!row.order.ORDER_ID) {
      return;
    }
    await this.revertPersistedSplitRow(row);
  }

  async clearSplitAmount(rowId: string): Promise<void> {
    const row = this.splitRows.find((r) => r.rowId === rowId);
    if (!row || row.order.ORDER_STATUS !== OrderStatusEnum.DRAFT) return;

    if (!row.order.ORDER_ID) {
      this.removeSplitRow(rowId);
      return;
    }

    const response = await this.connect.commitEvent('EVENT_DELETE_MUNI_ORDER', {
      DETAILS: { MUNI_ORDER_LIST: [mapMaturityOrderForDeletion(row.order)] },
    });
    if (response.ERROR?.length) {
      notifyApiError(response.ERROR[0]);
      return;
    }
    if (this.conditionsEditRowId === rowId) {
      this.closeConditionsPopup();
    }
    this.$emit('split-line-changed', { maturityId: this.sourceOrder?.MATURITY_ID });
    await this.refreshFromReqRep();
  }

  private async revertPersistedSplitRow(row: SplitOrderRow): Promise<void> {
    if (row.order.ORDER_STATUS === OrderStatusEnum.SUBMITTED_AND_DRAFT) {
      const response = await this.connect.commitEvent('EVENT_RESET_ORDERS_LIST', {
        DETAILS: { RESET_ORDERS_LIST: [mapMaturityOrderForSubmission(row.order)] },
      });
      if (response.ERROR?.length) {
        notifyApiError(response.ERROR[0]);
        return;
      }
    } else if (row.order.ORDER_STATUS === OrderStatusEnum.DRAFT) {
      const response = await this.connect.commitEvent('EVENT_DELETE_MUNI_ORDER', {
        DETAILS: { MUNI_ORDER_LIST: [mapMaturityOrderForDeletion(row.order)] },
      });
      if (response.ERROR?.length) {
        notifyApiError(response.ERROR[0]);
        return;
      }
    } else if (hasSubmittedAmountOnRecord(row.order)) {
      const response = await this.connect.commitEvent('EVENT_RESET_ORDERS_LIST', {
        DETAILS: { RESET_ORDERS_LIST: [mapMaturityOrderForSubmission(row.order)] },
      });
      if (response.ERROR?.length) {
        notifyApiError(response.ERROR[0]);
        return;
      }
    } else {
      this.commitSplitAmount(row.rowId, '');
      return;
    }
    this.$emit('split-line-changed', { maturityId: this.sourceOrder?.MATURITY_ID });
    const orderId = String(row.order.ORDER_ID ?? '').trim();
    await this.refreshFromReqRep(undefined, {
      syncPersistedFromServer: true,
      syncPersistedOrderIds: orderId ? [orderId] : undefined,
    });
  }

  closeModal(): void {
    this.stopStatusPolling();
    this.closeConditionsPopup();
    this.clearSinkingFundState();
    this.clearPrincipalAmountState();
    this.pendingSplitAmounts.clear();
    this._cachedSaveState = null;
    this.modalOpen = false;
    this.loading = false;
    this.removeAttribute('open');
    this.gridApi = null;
    this.modal?.close();
  }

  handleCancel(): void {
    this.closeModal();
  }

  get canSaveSplit(): boolean {
    void this.splitSaveEvaluationToken;
    if (!this.sourceOrder || this.loading) {
      return false;
    }
    return this.cachedSplitOrderSaveState.canSave;
  }

  get showRemoveSplit(): boolean {
    void this.splitSaveEvaluationToken;
    if (!this.sourceOrder || this.loading) {
      return false;
    }
    return shouldShowRemoveSplit(this.splitRows, this.baselineChildIds);
  }

  get splitOrderValidationMessage(): string | null {
    if (!this.sourceOrder || this.loading || this.canSaveSplit) {
      return null;
    }
    return this.cachedSplitOrderSaveState.validationMessage;
  }

  private notifySplitSaveEvaluationChanged(): void {
    this._cachedSaveState = null;
    this.splitSaveEvaluationToken += 1;
  }

  private _cachedSaveState: ReturnType<typeof getSplitOrderSaveState> | null = null;

  private get cachedSplitOrderSaveState() {
    if (!this._cachedSaveState) {
      this._cachedSaveState = getSplitOrderSaveState(
        this.sourceOrder,
        this.getSplitRowsWithPendingAmounts(),
        this.baselineChildIds,
        this.dealStatus,
      );
    }
    return this._cachedSaveState;
  }

  private getSplitRowsWithPendingAmounts(): SplitOrderRow[] {
    if (this.pendingSplitAmounts.size === 0) {
      return this.splitRows;
    }
    return this.splitRows.map((row) => {
      const pendingAmount = this.pendingSplitAmounts.get(row.rowId);
      if (pendingAmount == null || row.rowType !== 'split') {
        return row;
      }
      return { ...row, splitAmount: pendingAmount };
    });
  }

  handleSave(): void {
    if (!this.canSaveSplit) {
      return;
    }
    this.commitPendingSplitAmounts();
    this.syncSplitAmountsBeforeSave();
    this.$emit('save-split', {
      sourceOrder: this.sourceOrder,
      splitRows: this.splitRows.filter((r) => r.rowType === 'split' && !isSplitRowBlank(r)),
      baselineChildIds: [...this.baselineChildIds],
      baselineChildren: this.baselineChildren,
    });
  }

  private commitPendingSplitAmounts(): void {
    if (!this.gridContainer) return;
    this.gridContainer.querySelectorAll<MaturityAmountEntry>('maturity-amount-entry[data-row-id]').forEach(
      (entry) => {
        const rowId = entry.dataset.rowId;
        if (!rowId) return;
        const row = this.splitRows.find((splitRow) => splitRow.rowId === rowId);
        const value = entry.maturityInput?.value ?? String(entry.value ?? '');
        if (value.trim() === '' && row && resolveSplitRowQuantity(row) === '0') {
          return;
        }
        this.commitSplitAmount(rowId, value);
      },
    );
  }

  /** Align splitAmount with displayed All/cap rows before persisting. */
  private syncSplitAmountsBeforeSave(): void {
    let changed = false;
    const nextRows = this.splitRows.map((row) => {
      if (row.rowType !== 'split') return row;
      // Only sync All-on rows; ALL-cleared rows keep getOrderPatchAfterAllClear state intact.
      if (!row.order.IS_AUTO_RESET_ORDER_AMOUNT) return row;
      const effectiveQty = resolveSplitRowQuantity(row);
      if (effectiveQty === (row.splitAmount ?? '')) return row;
      changed = true;
      const { quantity, isZeroCancelIntent, shouldIgnore } = resolveManualOrderQuantityEdit(
        row.order,
        effectiveQty,
      );
      if (shouldIgnore) return row;
      const orderWithQuantity: MaturityOrder = {
        ...row.order,
        QUANTITY: quantity,
        IS_ZERO_CANCEL_INTENT: isZeroCancelIntent,
      };
      const splitAmount = formatOrderQuantityForCell(orderWithQuantity);
      return {
        ...row,
        splitAmount,
        order: {
          ...row.order,
          QUANTITY: quantity,
          IS_ZERO_CANCEL_INTENT: isZeroCancelIntent,
        },
      };
    });
    if (changed) {
      this.splitRows = nextRows;
    }
  }

  handleRemoveSplit(): void {
    this.confirmRemoveSplitModal?.show();
  }

  confirmRemoveSplit(): void {
    this.confirmRemoveSplitModal?.close();
    this.$emit('remove-split', { maturityId: this.sourceOrder?.MATURITY_ID });
  }

  cancelRemoveSplit(): void {
    this.confirmRemoveSplitModal?.close();
  }

  openConditionsForRow(row: SplitOrderRow): void {
    if (row.rowType !== 'split') return;
    const currentRow = this.splitRows.find((r) => r.rowId === row.rowId) ?? row;
    if (!isSplitRowEditable(currentRow)) return;
    this.conditionsEditRowId = currentRow.rowId;
    const quantity = resolveSplitRowQuantity(currentRow);
    this.orderEditConditions = { ...currentRow.order, QUANTITY: quantity };
    this.conditionsActive = true;
    this.refreshConditionsRowHighlight();
    requestAnimationFrame(() => {
      this.conditionsForm?.reflectExternalQuantity(this.orderEditConditions);
    });
  }

  closeConditionsPopup(): void {
    this.conditionsActive = false;
    this.orderEditConditions = null;
    this.conditionsEditRowId = null;
    this.refreshConditionsRowHighlight();
  }

  private refreshConditionsRowHighlight(): void {
    this.gridApi?.redrawRows();
  }

  handleOrderConditionsChanged(e: CustomEvent<MaturityOrder>): void {
    const updated = e.detail;
    if (!this.conditionsEditRowId) return;
    const editingRow = this.splitRows.find((r) => r.rowId === this.conditionsEditRowId);
    if (!editingRow || !isSplitRowEditable(editingRow)) return;
    const { quantity, isZeroCancelIntent, shouldIgnore } = resolveManualOrderQuantityEdit(
      { ...editingRow.order, ...updated },
      String(updated.QUANTITY ?? ''),
    );
    if (shouldIgnore) return;
    const orderWithQuantity: MaturityOrder = {
      ...updated,
      QUANTITY: quantity,
      IS_ZERO_CANCEL_INTENT: isZeroCancelIntent,
    };
    const splitAmount = formatOrderQuantityForCell(orderWithQuantity);
    const atCap = isFullParOrderAmountApplied({ ...updated, QUANTITY: splitAmount });
    const statusReference = editingRow.acceptedOrderBaseline ?? editingRow.order;
    this.splitRows = this.splitRows.map((r) =>
      r.rowId === this.conditionsEditRowId
        ? {
            ...r,
            splitAmount,
            order: {
              ...updated,
              QUANTITY: quantity,
              IS_ZERO_CANCEL_INTENT: isZeroCancelIntent,
              IS_AUTO_RESET_ORDER_AMOUNT: atCap ? (r.order.IS_AUTO_RESET_ORDER_AMOUNT ?? atCap) : false,
              ORDER_STATUS:
                getOrderStatusAfterSubmittedTrackConditionsEdit(statusReference, orderWithQuantity) ??
                this.resolveNextSplitRowOrderStatus(editingRow, quantity, isZeroCancelIntent) ??
                updated.ORDER_STATUS ??
                r.order.ORDER_STATUS ??
                (String(r.order.ORDER_ID ?? '').trim() === '' ? OrderStatusEnum.DRAFT : undefined),
            },
          }
        : r,
    );
    this.orderEditConditions =
      this.splitRows.find((row) => row.rowId === this.conditionsEditRowId)?.order ?? updated;
    this.updateGridData();
    this.refreshSplitAmountValidation(this.conditionsEditRowId);
    this.refreshRowActionCells();
    this.refreshOrderStatusCells();
  }

  private handleDocumentClick(e: Event): void {
    if (!this.modalOpen || !this.conditionsActive || !this.conditionsPopup) return;
    const path = e.composedPath();
    if (!path.includes(this.conditionsPopup)) {
      this.closeConditionsPopup();
    }
  }

  private handleSinkingFundCellMouseOver(event: CellMouseOverEvent<SplitOrderRow>): void {
    if (!event.data || event.data.rowType !== 'source') {
      return;
    }
    const order = event.data.order;
    const mouseEvent = event.event as MouseEvent | undefined;
    if (!mouseEvent) {
      return;
    }
    if (isZeroCouponMaturity(order?.SECURITY_TYPE) && hasZeroCouponPrincipalAmount(order)) {
      this.principalAmountValue = String(order.MATURITY_PRESENT_VALUE ?? '').trim();
      this.principalAmountHighlighted = shouldHighlightPrincipalAmountOnHover(
        order,
        this.dealStatus,
      );
      this.principalAmountAnchorTop = mouseEvent.clientY + 12;
      this.principalAmountAnchorLeft = mouseEvent.clientX + 12;
      this.principalAmountModalVisible = true;
      return;
    }
    if (!isTermBondMaturity(order?.SINKING_FUND_SCHEDULE)) {
      return;
    }
    this.clearSinkingFundHideTimeout();
    this.sinkingFundScheduleJson = order.SINKING_FUND_SCHEDULE ?? null;
    this.sinkingFundPanelStyle = computeSinkingFundPanelLayout(mouseEvent);
    this.sinkingFundPanelHovered = false;
    this.sinkingFundModalVisible = true;
  }

  handleSinkingFundPanelMouseEnter(): void {
    this.clearSinkingFundHideTimeout();
    this.sinkingFundPanelHovered = true;
  }

  handleSinkingFundPanelMouseLeave(): void {
    this.sinkingFundPanelHovered = false;
    this.sinkingFundModalVisible = false;
    this.sinkingFundScheduleJson = null;
  }

  private clearSinkingFundHideTimeout(): void {
    if (this.sinkingFundHideTimeout !== null) {
      clearTimeout(this.sinkingFundHideTimeout);
      this.sinkingFundHideTimeout = null;
    }
  }

  private handleSinkingFundCellMouseOut(): void {
    if (this.sinkingFundModalVisible) {
      this.clearSinkingFundHideTimeout();
      this.sinkingFundHideTimeout = window.setTimeout(() => {
        this.sinkingFundHideTimeout = null;
        if (!this.sinkingFundPanelHovered) {
          this.sinkingFundModalVisible = false;
          this.sinkingFundScheduleJson = null;
        }
      }, 150);
    }
    this.principalAmountModalVisible = false;
    this.principalAmountValue = null;
    this.principalAmountHighlighted = false;
  }

  private clearSinkingFundState(): void {
    this.clearSinkingFundHideTimeout();
    this.sinkingFundModalVisible = false;
    this.sinkingFundScheduleJson = null;
    this.sinkingFundPanelStyle = '';
    this.sinkingFundPanelHovered = false;
  }

  private clearPrincipalAmountState(): void {
    this.principalAmountModalVisible = false;
    this.principalAmountValue = null;
    this.principalAmountHighlighted = false;
    this.principalAmountAnchorTop = 0;
    this.principalAmountAnchorLeft = 0;
  }

  private updateGridData(scrollToRowId?: string): void {
    if (!this.gridApi) return;
    const sourceRow = this.splitRows.find((row) => row.rowType === 'source');
    this.gridApi.setPinnedTopRowData(sourceRow ? [sourceRow] : []);
    this.gridApi.setRowData(this.splitRows.filter((row) => row.rowType === 'split'));
    this.updateGridLayout(scrollToRowId);
  }

  private updateGridLayout(scrollToRowId?: string): void {
    if (!this.gridContainer) return;
    const taxStatus = this.taxStatus as TaxStatusEnum;
    const gridWidth = getSplitOrderGridMinWidth(taxStatus, this.securityType);
    this.gridContainer.style.width = `${gridWidth}px`;
    this.gridContainer.style.maxWidth = '100%';
    this.style.setProperty(
      '--split-order-modal-width',
      `${gridWidth + SPLIT_ORDER_MODAL_HORIZONTAL_PADDING}px`,
    );

    if (!this.gridApi) return;

    const actualSplitCount = this.splitRows.filter((row) => row.rowType === 'split').length;
    const bodyRowSlots = actualSplitCount > 0 ? actualSplitCount : 3;
    const hasSource = this.splitRows.some((row) => row.rowType === 'source');
    const naturalHeight =
      GRID_HEADER_HEIGHT +
      (hasSource ? GRID_ROW_HEIGHT : 0) +
      bodyRowSlots * GRID_ROW_HEIGHT +
      GRID_SCROLLBAR_BUFFER_PX;
    const maxHeight =
      Math.min(SPLIT_ORDER_MODAL_MAX_HEIGHT, window.innerHeight * 0.92) -
      SPLIT_ORDER_MODAL_NON_GRID_PX;

    this.gridApi.setDomLayout('normal');
    this.gridContainer.style.height = `${Math.min(naturalHeight, maxHeight)}px`;

    if (!scrollToRowId) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const rowNode = this.gridApi?.getRowNode(scrollToRowId);
        rowNode && this.gridApi?.ensureNodeVisible(rowNode, 'bottom');
      });
    });
  }

  /** Refresh dependent columns without re-rendering the active amount input. */
  private refreshRowActionCells(): void {
    if (!this.gridApi) return;
    this.gridApi.refreshCells({
      columns: ['rowActions'],
      force: true,
    });
    const sourceNode = this.gridApi.getRowNode('source');
    if (sourceNode) {
      this.gridApi.refreshCells({
        rowNodes: [sourceNode],
        columns: ['splitAmount'],
        force: true,
      });
    }
  }

  private refreshOrderStatusCells(): void {
    if (!this.gridApi) return;
    this.gridApi.refreshCells({
      columns: ['orderStatus', 'rowActions', 'conditions'],
      force: true,
    });
    const sourceNode = this.gridApi.getRowNode('source');
    if (sourceNode) {
      this.gridApi.refreshCells({
        rowNodes: [sourceNode],
        columns: ['orderStatus'],
        force: true,
      });
    }
  }

  private getSplitAmountValidationOrder(row: SplitOrderRow | undefined): MaturityOrder | undefined {
    if (!row) return undefined;
    if (row.rowType === 'source') {
      const total = getSplitParentQuantityTotal(this.splitRows);
      if (!total || !this.sourceOrder) return undefined;
      return {
        ...this.sourceOrder,
        QUANTITY: total,
        HAS_SPLIT: true,
        IS_SPLIT_MATURITY: true,
      };
    }
    return getSplitRowValidationOrder(row);
  }

  private setGridOptions(): void {
    const taxStatus = this.taxStatus as TaxStatusEnum;
    const readOnlyCellStyle = { ...lightGreyCell };
    const numericReadOnlyCellStyle = { ...lightGreyCell, textAlign: 'right' as const };
    const getIsInRepriceWindow = () => isRepriceWindow(this.dealStatus);
    const quantityValidationColDef = getQuantityColumnValidationColDef({
      getDealStatus: () => this.dealStatus,
      includePinnedRowsInValidation: true,
      getValidationOrder: (params) => {
        const rowId = (params.data as SplitOrderRow | undefined)?.rowId;
        const row =
          rowId != null
            ? this.splitRows.find((splitRow) => splitRow.rowId === rowId) ??
              (params.data as SplitOrderRow)
            : (params.data as SplitOrderRow);
        return this.getSplitAmountValidationOrder(row);
      },
    });

    const columnDefs: ColDef<SplitOrderRow>[] = [
      ...buildSplitOrderReadOnlyColumnDefs({
        taxStatus,
        securityType: this.securityType,
        getIsInRepriceWindow,
        readOnlyCellStyle,
        numericReadOnlyCellStyle,
        onRemoveRow: (rowId: string) => this.removeSplitRow(rowId),
      }),
      {
        colId: 'splitAmount',
        headerName: 'Order Amount ($M)',
        headerClass: 'ag-right-aligned-header',
        ...getFixedWidthConfig(SPLIT_ORDER_COL_WIDTHS.splitAmount),
        cellClassRules: {
          ...quantityValidationColDef.cellClassRules,
          'split-amount-total-cell': (params) => params.data?.rowType === 'source',
          'hover-effect': (params) => params.data?.rowType === 'split',
          'focus-border': (params) => params.data?.rowType === 'split',
        },
        cellStyle: (params) => ({
          ...(typeof quantityValidationColDef.cellStyle === 'function'
            ? quantityValidationColDef.cellStyle(params)
            : quantityValidationColDef.cellStyle),
          borderLeft: dealSeriesBorder,
        }),
        tooltipValueGetter: quantityValidationColDef.tooltipValueGetter,
        valueGetter: (params) => {
          if (params.data?.rowType === 'source') {
            return formatSplitOrderAmountTotal(this.splitRows);
          }
          const row = params.data;
          if (!row) return '';
          return formatOrderQuantityForCell({ ...row.order, QUANTITY: row.splitAmount });
        },
        cellRenderer: SplitOrderAmountCellRenderer,
        cellRendererParams: {
          onAmountCommit: (rowId: string, value: string) => this.commitSplitAmount(rowId, value),
          onAmountDraft: (rowId: string, value: string) => {
            this.pendingSplitAmounts.set(rowId, value);
            this.notifySplitSaveEvaluationChanged();
          },
          getMaturityAmount: () => getOrderAmountCap(this.sourceOrder),
          getValidationState: (rowId: string, amount: string) =>
            this.getSplitRowQuantityValidationState(rowId, amount),
          onAllClicked: (rowId: string, isAll: boolean) => this.applyAllSplitAmount(rowId, isAll),
          isParentAllActive: () => areAllSplitChildrenAtFullCap(this.sourceOrder, this.splitRows),
          getParentAmountTotal: () => formatSplitOrderAmountTotal(this.splitRows),
        },
      },
      {
        ...conditionsSummaryColDef,
        colId: 'conditions',
        field: undefined,
        cellRendererSelector: undefined,
        ...getFixedWidthConfig(SPLIT_ORDER_COL_WIDTHS.conditions),
        cellClass: 'conditions-summary-cell focus-border',
        cellClassRules: {
          'hover-effect': (params) => params.data?.rowType === 'split',
        },
        cellStyle: (params) =>
          params.data?.rowType === 'split' && isSplitRowEditable(params.data)
            ? { ...orderEntryCellStyle, cursor: 'pointer' }
            : { ...orderEntryCellStyle, cursor: 'default' },
        cellRenderer: SplitOrderConditionsCellRenderer,
        cellRendererParams: {
          onConditionsClick: (row: SplitOrderRow) => this.openConditionsForRow(row),
          getSourceConditionsOrder: () => getSplitConditionsRollupOrder(this.sourceOrder, this.splitRows),
        },
        tooltipValueGetter: (params) => {
          if (params.data?.rowType === 'source') {
            return conditionsTooltipFormatter(
              getSplitConditionsRollupOrder(this.sourceOrder, this.splitRows),
              this.flexMaturityOptions,
            );
          }
          return conditionsTooltipFormatter(params.data?.order, this.flexMaturityOptions);
        },
      },
      {
        ...statusColDef,
        colId: 'orderStatus',
        field: 'order.ORDER_STATUS',
        headerName: 'Order Status',
        ...getFixedWidthConfig(SPLIT_ORDER_COL_WIDTHS.orderStatus),
        cellClassRules: {
          'focus-border': (params) => params.data?.rowType === 'split',
        },
        cellStyle: () => ({
          ...lightGreyCell,
          borderLeft: dealSeriesBorder,
        }),
        cellRendererParams: (params: ICellRendererParams<SplitOrderRow>) =>
          getSplitRowStatusIconConfig(params, this.dealStatus, this.sourceOrder, this.splitRows),
        tooltipValueGetter: (params) => {
          const order = getSplitRowStatusTooltipOrder(params.data, this.sourceOrder, this.splitRows);
          if (!order) {
            return null;
          }
          if (order.ERROR_MESSAGE) {
            if (order.ORDER_STATUS != null && successOrderStatuses.has(order.ORDER_STATUS)) {
              return `Warning from Dealer: [ ${order.ERROR_MESSAGE} ]`;
            }
            return order.ERROR_MESSAGE;
          }
          if (
            order.IS_CONFIRMED === true &&
            order.ORDER_STATUS != null &&
            successOrderStatuses.has(order.ORDER_STATUS)
          ) {
            return 'Order has been reconfirmed';
          }
          return null;
        },
      },
      {
        colId: 'rowActions',
        headerName: '',
        ...getFixedWidthConfig(SPLIT_ORDER_COL_WIDTHS.rowActions),
        cellClass: 'focus-border',
        cellStyle: () => ({
          ...orderEntryCellStyle,
          borderLeft: dealSeriesBorder,
          borderRight: dealSeriesBorder,
        }),
        cellRenderer: SplitOrderRowActionsCellRenderer,
        cellRendererParams: () => ({
          onResetAmount: (rowId: string) => this.resetSplitAmount(rowId),
          onClearAmount: (rowId: string) => this.clearSplitAmount(rowId),
          dealStatus: this.dealStatus,
          inRepriceWindow: isRepriceWindow(this.dealStatus),
          dealHasEligibleOrdersToReset: this.dealHasEligibleOrdersToReset,
          getSourceOrder: () => this.sourceOrder,
        }),
      },
    ];

    this.addSplitOffset = getSplitOrderAddSplitOffset(taxStatus, this.securityType);

    this.gridOptions = {
      columnDefs,
      rowData: [],
      pinnedTopRowData: [],
      headerHeight: GRID_HEADER_HEIGHT,
      rowHeight: GRID_ROW_HEIGHT,
      tooltipShowDelay: 0,
      suppressMovableColumns: true,
      suppressContextMenu: true,
      suppressColumnVirtualisation: true,
      suppressScrollOnNewData: true,
      defaultColDef: {
        sortable: false,
        filter: false,
        resizable: false,
        editable: false,
        suppressMenu: true,
        flex: 0,
      },
      getRowId: (params) => params.data.rowId,
      getRowClass: (params) => {
        const classes: string[] = [];
        if (params.data?.rowType === 'source' || params.node?.rowPinned === 'top') {
          classes.push('split-parent-no-hover');
        }
        if (this.conditionsActive && params.data?.rowId === this.conditionsEditRowId) {
          classes.push('conditions-modal-open');
        }
        return classes.length ? classes.join(' ') : undefined;
      },
      tabToNextCell: (params) => {
        const { previousCellPosition, backwards, api, columnApi } = params;
        const { column, rowIndex, rowPinned } = previousCellPosition;
        if (column.getColId() === 'splitAmount' && rowPinned == null) {
          const nextRow = backwards ? rowIndex - 1 : rowIndex + 1;
          const rowCount = api.getDisplayedRowCount();
          return nextRow >= 0 && nextRow < rowCount
            ? { rowIndex: nextRow, column, rowPinned: null }
            : null;
        }

        const columns = columnApi.getAllDisplayedColumns();
        const colIndex = columns.indexOf(column);
        let nextCol = backwards ? colIndex - 1 : colIndex + 1;
        let nextRow = rowIndex;

        if (nextCol >= columns.length) {
          nextCol = 0;
          nextRow++;
        } else if (nextCol < 0) {
          nextCol = columns.length - 1;
          nextRow--;
        }

        if (nextRow < 0 || nextRow >= api.getDisplayedRowCount()) return null;

        return {
          rowIndex: nextRow,
          column: columns[nextCol],
          rowPinned: rowPinned ?? null,
        };
      },
      onCellClicked: (event) => {
        if (event.data?.rowType === 'source') {
          event.api.clearFocusedCell();
        }
      },
      onCellFocused: (event) => {
        if (event.rowPinned === 'top') {
          event.api.clearFocusedCell();
          return;
        }
        const colId =
          typeof event.column === 'string' ? event.column : event.column?.getColId?.();
        if (colId !== 'splitAmount' || event.rowIndex == null) {
          return;
        }
        const rowNode = event.api.getDisplayedRowAtIndex(event.rowIndex);
        if (!rowNode) return;
        const instances = event.api.getCellRendererInstances({
          rowNodes: [rowNode],
          columns: ['splitAmount'],
        });
        (instances?.[0] as SplitOrderAmountCellRenderer | undefined)?.focusInput?.();
      },
      onCellMouseOver: (event) => {
        if (event.column?.getColId() !== 'MATURITY_AMOUNT') {
          return;
        }
        this.handleSinkingFundCellMouseOver(event);
      },
      onCellMouseOut: (event) => {
        if (event.column?.getColId() !== 'MATURITY_AMOUNT') {
          return;
        }
        this.handleSinkingFundCellMouseOut();
      },
    };

    if (this.gridApi) {
      this.gridApi.setColumnDefs(columnDefs);
    }

    this.updateGridLayout();
  }
}
